import React from 'react';
import clsx from 'clsx';
import { isEqual } from 'lodash';
import { t } from 'i18next';
import { parseDateInTimeZone, timeZone, addMinutes } from '@brighterion/utils/date-timezone';
import usePostScheduledDeployment from 'Hooks/query-hooks/scheduled-deployment/usePostScheduledDeployment';
import usePutScheduledDeployment from 'Hooks/query-hooks/scheduled-deployment/usePutScheduledDeployment';
import { Modal, RadioButton, Textarea } from '@brighterion/br-ui';
import { Field, FieldProps, Form, Formik } from 'formik';
import type { TemplateConfig } from '@brighterion/types/config';
import type { ScheduledDeploymentSchema } from '@brighterion/types/scheduled-deployment';
import { RQ_SCHEDULED_DEPLOYMENT_SUMMARY } from '@brighterion/utils/react-query-constants';
import { useQueryClient } from '@tanstack/react-query';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { DATE_PICKER_PATTERN, SCHEDULE_DEPLOYMENT_DATE_FORMAT } from '@brighterion/utils/constants';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import type { ScheduledDeploymentContentProps } from '../ScheduledDeploymentContent';
import { scheduledDeploymentSchema } from './scheduledDeploymentSchema';

import styles from './ScheduledDeploymentForm.css';

type ScheduledDeploymentFormProps = {
    children?: React.ReactNode;
    initialFormValues: ScheduledDeploymentSchema;
    locale: TemplateConfig['locale'];
} & ScheduledDeploymentContentProps;

const ScheduledDeploymentForm = ({
    children,
    closeModal,
    initialFormValues,
    modal,
    status,
    rulesType
}: ScheduledDeploymentFormProps) => {
    const postMutation = usePostScheduledDeployment();
    const putMutation = usePutScheduledDeployment();
    const queryClient = useQueryClient();
    const { locale } = useTemplateStore.getState().template;

    const { id, name, title } = modal;

    const initialValues: ScheduledDeploymentSchema = {
        comment: initialFormValues.comment,
        deployNow: initialFormValues.deployNow,
        scheduledTime: initialFormValues.scheduledTime
            ? parseDateInTimeZone(new Date(initialFormValues.scheduledTime), timeZone, SCHEDULE_DEPLOYMENT_DATE_FORMAT)
            : parseDateInTimeZone(addMinutes(new Date(), 7), timeZone, SCHEDULE_DEPLOYMENT_DATE_FORMAT),
        templateRepositoryId: initialFormValues.templateRepositoryId
    };

    return (
        <Formik
            enableReinitialize
            validateOnMount
            initialValues={initialValues}
            validationSchema={scheduledDeploymentSchema(locale)}
            onSubmit={async (formValues, actions) => {
                /** If deploy now is true, remove scheduledTime */
                let values = formValues;
                if (formValues.deployNow) {
                    values = { ...formValues, scheduledTime: undefined };
                }
                /** Refetch summary for changes */
                await queryClient.refetchQueries([RQ_SCHEDULED_DEPLOYMENT_SUMMARY]);
                const summary = queryClient.getQueryData<ScheduledDeploymentSummary>([RQ_SCHEDULED_DEPLOYMENT_SUMMARY]);

                if (modal.operation === 'create') {
                    /** Only POST schedule deployment if summary is equal */
                    if (isEqual(summary?.current, summary?.previous)) {
                        postMutation.mutate(values, {
                            onSuccess: () => closeModal(true),
                            onError: () => actions.setSubmitting(false)
                        });

                        switch (rulesType) {
                            case 'globalRules': {
                                trackEvent({
                                    eventType: EventTypes.track,
                                    eventName: EventNames.click,
                                    payload: {
                                        clickIdentifier: formValues.deployNow
                                            ? 'Review Deployment - Deploy'
                                            : 'Review Deployment - Schedule Deployment',
                                        text: formValues.deployNow
                                            ? 'Deploy Global Rules Sandbox'
                                            : 'Schedule Global Rules Deployment'
                                    }
                                });
                                break;
                            }
                            case 'merchantOverrides': {
                                trackEvent({
                                    eventType: EventTypes.track,
                                    eventName: EventNames.click,
                                    payload: {
                                        clickIdentifier: 'Review Deployment - Schedule Deployment',
                                        text: 'Subject Schedule Deployment'
                                    }
                                });
                                break;
                            }
                            default: {
                                break;
                            }
                        }
                    }
                }

                if (modal.operation === 'update') {
                    putMutation.mutate(values, {
                        onSuccess: () => closeModal(),
                        onError: () => actions.setSubmitting(false)
                    });
                }
            }}
        >
            {({ values, handleChange, handleBlur, isSubmitting, handleSubmit, setFieldValue, isValid }) => {
                /** Toggle between Button labels when user is selecting the deployment type */
                const buttonLabel = values.deployNow ? t('Deploy') : t('Schedule Deployment');
                return (
                    <Form>
                        <Modal.Title id={id + name} onClose={closeModal} statusIcon={status} title={title} />
                        <div className={styles.container}>{children}</div>
                        <div className={styles.content}>
                            <Field name='comment'>
                                {({ field, form: { errors, touched } }: FieldProps) => {
                                    return (
                                        <Textarea
                                            className={styles.textarea}
                                            label={t('Comment (Optional)')}
                                            lineNum={4}
                                            showCounter={false}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            maxChar={150}
                                            value={field.value}
                                            name='comment'
                                            error={Boolean(errors[field.name] && touched[field.name])}
                                            errorMsg={errors[field.name]?.toString()}
                                            allowPan
                                        />
                                    );
                                }}
                            </Field>
                            <div
                                role='radiogroup'
                                aria-labelledby='select-deployment-type'
                                className={styles.radiogroup}
                            >
                                {/** Accessibility */}
                                <div id='select-deployment-type' hidden>
                                    Select deployment type
                                </div>
                                <RadioButton
                                    name='selectDeploymentType'
                                    text='Deploy now'
                                    id='deploy-now'
                                    value='true'
                                    onChange={() => setFieldValue('deployNow', true, true)}
                                    aria-labelledby='now'
                                    className={styles.radiobutton}
                                    checked={values.deployNow}
                                />
                                <RadioButton
                                    name='selectDeploymentType'
                                    text='Schedule for a later time'
                                    id='scheduled-deployment'
                                    value='false'
                                    onChange={() => setFieldValue('deployNow', false, true)}
                                    aria-labelledby='scheduled'
                                    className={styles.radiobutton}
                                    checked={!values.deployNow}
                                />
                            </div>
                            {!values.deployNow && (
                                <Field name='scheduledTime'>
                                    {({ field, form: { errors } }: FieldProps) => {
                                        return (
                                            <>
                                                <input
                                                    {...field}
                                                    type='datetime-local'
                                                    value={String(field.value)}
                                                    min={parseDateInTimeZone(
                                                        new Date(),
                                                        timeZone,
                                                        SCHEDULE_DEPLOYMENT_DATE_FORMAT
                                                    )}
                                                    pattern={DATE_PICKER_PATTERN}
                                                    className={clsx([
                                                        styles.datetime,
                                                        errors[field.name] && styles.inputError
                                                    ])}
                                                />
                                                {errors[field.name] && (
                                                    <div className={styles.labelError}>{errors[field.name]}</div>
                                                )}
                                            </>
                                        );
                                    }}
                                </Field>
                            )}
                        </div>
                        <Modal.Footer
                            id={id + name}
                            onClose={closeModal}
                            onActionText={buttonLabel}
                            disableOnAction={
                                !isValid || isSubmitting || postMutation.isLoading || putMutation.isLoading
                            }
                            onAction={handleSubmit}
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ScheduledDeploymentForm;
